import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  IconButton,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemButton
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  addDoc, 
  getDocs, 
  doc, 
  Timestamp, 
  getDoc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate, useLocation } from 'react-router-dom';

const MessageCenter = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const unsubscribeRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Récupérer le paramètre contact de l'URL
  const searchParams = new URLSearchParams(location.search);
  const contactIdFromUrl = searchParams.get('contact');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Nettoyer l'écouteur lors du démontage du composant
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  // Charger les contacts
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        if (!userData?.establishmentId) {
          throw new Error('Données utilisateur manquantes');
        }

        setLoading(true);

        const usersRef = collection(db, 'users');
        const usersQuery = query(
          usersRef,
          where('establishmentId', '==', userData.establishmentId),
          where('role', 'in', ['chefService', 'personnel', 'parent', 'student'])
        );

        const usersSnapshot = await getDocs(usersQuery);
        const usersList = usersSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(user => user.id !== currentUser.uid);

        setContacts(usersList);
        
        // Si un contactId est spécifié dans l'URL, sélectionner ce contact
        if (contactIdFromUrl) {
          
          // Essayer de trouver le contact par id ou par uid
          let contactFromUrl = usersList.find(user => user.id === contactIdFromUrl);
          
          // Si pas trouvé par id, essayer par uid
          if (!contactFromUrl) {
            contactFromUrl = usersList.find(user => user.uid === contactIdFromUrl);
          }
          
          
          if (contactFromUrl) {
            setSelectedContact(contactFromUrl);
          } else {
            // Essayer de récupérer l'utilisateur directement depuis Firestore
            const fetchUserById = async () => {
              try {
                const userDoc = await getDoc(doc(db, 'users', contactIdFromUrl));
                if (userDoc.exists()) {
                  const userData = { id: userDoc.id, ...userDoc.data() };
                  setSelectedContact(userData);
                  // Ajouter l'utilisateur à la liste des contacts
                  setContacts(prev => [...prev, userData]);
                } else {
                }
              } catch (err) {
                console.error('Erreur lors de la récupération de l\'utilisateur:', err);
              }
            };
            fetchUserById();
          }
        }
        
        setLoading(false);
      } catch (error) {
        setError('Erreur lors du chargement des contacts');
        setLoading(false);
      }
    };

    if (userData) {
      fetchContacts();
    }
  }, [userData, currentUser, contactIdFromUrl]);

  // Filtrer et trier les contacts
  const filteredContacts = contacts
    .filter(contact => {
      // Filtre de recherche
      const searchMatch = `${contact.firstName} ${contact.lastName} ${contact.department || ''}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      
      // Filtre de rôle
      const roleMatch = roleFilter === 'all' || contact.role === roleFilter;
      
      return searchMatch && roleMatch;
    })
    .sort((a, b) => {
      // Tri par rôle
      const roleOrder = {
        chefService: 1,
        personnel: 2,
        parent: 3,
        student: 4
      };
      
      if (roleOrder[a.role] !== roleOrder[b.role]) {
        return roleOrder[a.role] - roleOrder[b.role];
      }
      
      // Tri par département pour le personnel et les chefs de service
      if ((a.role === 'chefService' || a.role === 'personnel') &&
          (b.role === 'chefService' || b.role === 'personnel') &&
          a.department !== b.department) {
        // Vérifier que les deux départements existent avant de comparer
        if (a.department && b.department) {
          return a.department.localeCompare(b.department);
        }
        // Si un département est manquant, placer l'utilisateur sans département à la fin
        return a.department ? -1 : 1;
      }
      
      // Tri par nom
      return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    });

  // Mettre à jour l'URL lorsqu'un contact est sélectionné
  useEffect(() => {
    if (selectedContact) {
      // Mettre à jour l'URL sans recharger la page
      const newUrl = `${location.pathname}?contact=${selectedContact.id}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [selectedContact, location.pathname]);
  
  // Écouter les messages en temps réel lorsqu'un contact est sélectionné
  useEffect(() => {
    if (!selectedContact || !userData?.establishmentId) return;

    // Désabonner de l'ancien écouteur si il existe
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    const messagesRef = collection(db, 'messages');
    const messagesQuery = query(
      messagesRef,
      where('establishmentId', '==', userData.establishmentId),
      orderBy('createdAt', 'asc')
    );

    // Mettre en place l'écouteur en temps réel
    unsubscribeRef.current = onSnapshot(messagesQuery, (snapshot) => {
      const messagesList = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(msg => 
          (msg.senderId === currentUser.uid && msg.recipientId === selectedContact.id) ||
          (msg.senderId === selectedContact.id && msg.recipientId === currentUser.uid)
        );

      setMessages(messagesList);
      scrollToBottom();
    }, (error) => {
      setError('Erreur lors de la réception des messages: ' + error.message);
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [selectedContact, userData, currentUser]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    // Vérifier si nous sommes en mode sélection multiple ou simple
    const recipients = multiSelectMode ? selectedContacts : (selectedContact ? [selectedContact] : []);
    
    if (!newMessage.trim() || recipients.length === 0 || !userData?.establishmentId) {
      setError('Veuillez sélectionner au moins un destinataire et écrire un message');
      return;
    }

    try {
      // Créer un tableau de promesses pour envoyer le message à chaque destinataire
      const sendPromises = recipients.map(recipient => {
        const messageData = {
          content: newMessage,
          senderId: currentUser.uid,
          senderName: `${userData.firstName} ${userData.lastName}`,
          senderRole: userData.role,
          recipientId: recipient.id,
          recipientName: `${recipient.firstName} ${recipient.lastName}`,
          recipientRole: recipient.role,
          establishmentId: userData.establishmentId,
          createdAt: Timestamp.now(),
          read: false
        };
        
        return addDoc(collection(db, 'messages'), messageData);
      });
      
      // Attendre que tous les messages soient envoyés
      await Promise.all(sendPromises);
      
      // Réinitialiser le message
      setNewMessage('');
      
      // Afficher une confirmation
      setError(null);
      alert(`Message envoyé avec succès à ${recipients.length} destinataire(s)`);
    } catch (error) {
      setError('Erreur lors de l\'envoi du message: ' + error.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Bouton retour */}
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin/dashboard')}
        sx={{ mb: 2 }}
      >
        Retour au tableau de bord
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)', position: 'relative' }}>
        {/* Liste des contacts - colonne fixe à gauche */}
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Paper sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  Contacts
                </Typography>
                <Button
                  variant={multiSelectMode ? "contained" : "outlined"}
                  color={multiSelectMode ? "primary" : "inherit"}
                  size="small"
                  onClick={() => {
                    setMultiSelectMode(!multiSelectMode);
                    if (!multiSelectMode) {
                      // Activer le mode sélection multiple
                      setSelectedContacts(selectedContact ? [selectedContact] : []);
                    } else {
                      // Désactiver le mode sélection multiple
                      setSelectedContact(selectedContacts.length === 1 ? selectedContacts[0] : null);
                      setSelectedContacts([]);
                    }
                  }}
                >
                  {multiSelectMode ? "Mode normal" : "Sélection multiple"}
                </Button>
              </Box>
              
              {/* Filtres */}
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>Filtrer par rôle</InputLabel>
                  <Select
                    value={roleFilter}
                    label="Filtrer par rôle"
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <MenuItem value="all">Tous les contacts</MenuItem>
                    <MenuItem value="chefService">Chefs de service</MenuItem>
                    <MenuItem value="personnel">Personnel</MenuItem>
                    <MenuItem value="parent">Parents</MenuItem>
                    <MenuItem value="student">Étudiants</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Rechercher un contact..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                />
              </Box>
            </Box>

            {/* Liste des contacts */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ flex: 1, overflow: 'auto' }}>
                {filteredContacts.length === 0 ? (
                  <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    Aucun contact trouvé
                  </Typography>
                ) : (
                  filteredContacts.map((contact) => (
                    <ListItem
                      key={contact.id}
                      disablePadding
                      sx={{ px: 2, py: 0.5 }}
                    >
                      <ListItemButton
                        onClick={() => {
                          if (multiSelectMode) {
                            // Mode sélection multiple
                            setSelectedContacts(prev => {
                              const isSelected = prev.some(c => c.id === contact.id);
                              if (isSelected) {
                                return prev.filter(c => c.id !== contact.id);
                              } else {
                                return [...prev, contact];
                              }
                            });
                          } else {
                            // Mode sélection simple
                            setSelectedContact(contact);
                            
                            // Mettre à jour l'URL
                            const newSearchParams = new URLSearchParams(location.search);
                            newSearchParams.set('contact', contact.id);
                            navigate(`${location.pathname}?${newSearchParams.toString()}`);
                          }
                        }}
                        selected={multiSelectMode 
                          ? selectedContacts.some(c => c.id === contact.id)
                          : selectedContact?.id === contact.id
                        }
                        sx={{
                          borderRadius: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            }
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: (() => {
                                switch(contact.role) {
                                  case 'chefService': return 'error.main';
                                  case 'personnel': return 'primary.main';
                                  case 'parent': return 'success.main';
                                  case 'student': return 'info.main';
                                  default: return 'grey.500';
                                }
                              })()
                            }}
                          >
                            {contact.firstName?.[0]?.toUpperCase() || <PersonIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        
                        <ListItemText
                          primary={`${contact.firstName} ${contact.lastName}`}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {/* Rôle */}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  color: 'inherit',
                                  opacity: 0.8
                                }}
                              >
                                {(() => {
                                  switch(contact.role) {
                                    case 'chefService': return 'Chef de service';
                                    case 'personnel': return 'Personnel';
                                    case 'parent': return 'Parent';
                                    case 'student': return 'Étudiant';
                                    default: return contact.role;
                                  }
                                })()}
                              </Typography>
                              
                              {/* Département (si applicable) */}
                              {(contact.role === 'chefService' || contact.role === 'personnel') && contact.department && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    backgroundColor: 'action.selected',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    color: 'text.secondary'
                                  }}
                                >
                                  {contact.department}
                                </Typography>
                              )}
                            </Box>
                          }
                          primaryTypographyProps={{
                            variant: 'subtitle2',
                            fontWeight: 'medium'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Zone de chat */}
        <Grid item xs={12} md={8} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {multiSelectMode ? (
              // Mode multi-sélection
              <>
                {/* En-tête du chat en mode multi-sélection */}
                <Box sx={{ 
                  p: 2, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 1
                }}>
                  <Typography variant="h6">
                    Message groupé ({selectedContacts.length} destinataire{selectedContacts.length > 1 ? 's' : ''})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {selectedContacts.map(contact => (
                      <Box
                        key={contact.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          fontSize: '0.8rem'
                        }}
                      >
                        {contact.firstName} {contact.lastName}
                      </Box>
                    ))}
                  </Box>
                </Box>
                
                {/* Zone de messages en mode multi-sélection */}
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  p: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 2,
                  height: 'calc(100% - 140px)',
                  mb: '70px'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body1" color="text.secondary" align="center">
                      Mode message groupé activé.<br />
                      Sélectionnez vos destinataires et rédigez votre message ci-dessous.
                    </Typography>
                  </Box>
                  <div ref={messagesEndRef} />
                </Box>
                
                {/* Zone de saisie en mode multi-sélection */}
                <Box
                  component="form"
                  onSubmit={handleSendMessage}
                  sx={{
                    p: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1
                  }}
                >
                  {/* Indicateur de destinataires */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium' }}>
                      {selectedContacts.length === 0 ? (
                        <span style={{ color: 'red' }}>Aucun destinataire sélectionné</span>
                      ) : (
                        `${selectedContacts.length} destinataire${selectedContacts.length > 1 ? 's' : ''} sélectionné${selectedContacts.length > 1 ? 's' : ''}`
                      )}
                    </Typography>
                  </Box>
                  
                  {/* Zone de saisie et bouton d'envoi */}
                  <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Message groupé..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      multiline
                      maxRows={4}
                    />
                    <IconButton
                      color="primary"
                      type="submit"
                      disabled={!newMessage.trim() || selectedContacts.length === 0}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </>
            ) : selectedContact ? (
              <>
                {/* En-tête du chat en mode normal - fixe en haut */}
                <Box sx={{ 
                  p: 2, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 1
                }}>
                  <Typography variant="h6">
                    {selectedContact.firstName} {selectedContact.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedContact.role}
                  </Typography>
                </Box>

                {/* Messages - zone défilante indépendante */}
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  p: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 2,
                  height: 'calc(100% - 140px)', /* Hauteur calculée pour laisser de la place pour l'en-tête et la zone de saisie */
                  mb: '70px' /* Marge en bas pour éviter que les derniers messages soient cachés par la zone de saisie */
                }}>
                  {multiSelectMode ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary" align="center">
                        Mode message groupé activé.<br />
                        Sélectionnez vos destinataires et rédigez votre message ci-dessous.
                      </Typography>
                    </Box>
                  ) : (
                    messages.map((message) => {
                      const isCurrentUser = message.senderId === currentUser.uid;
                      return (
                        <Box
                          key={message.id}
                          sx={{
                            display: 'flex',
                            justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                            mb: 1
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '70%',
                              bgcolor: isCurrentUser ? 'primary.main' : 'grey.100',
                              color: isCurrentUser ? 'white' : 'text.primary',
                              borderRadius: 2,
                              p: 2,
                              position: 'relative'
                            }}
                          >
                            <Typography variant="body1">
                              {message.content}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                              {format(message.createdAt.toDate(), 'HH:mm', { locale: fr })}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Zone de saisie - fixe en bas */}
                <Box
                  component="form"
                  onSubmit={handleSendMessage}
                  sx={{
                    p: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1
                  }}
                >
                  {/* Indicateur de destinataires en mode multi-sélection */}
                  {multiSelectMode && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium' }}>
                        {selectedContacts.length === 0 ? (
                          <span style={{ color: 'red' }}>Aucun destinataire sélectionné</span>
                        ) : (
                          `${selectedContacts.length} destinataire${selectedContacts.length > 1 ? 's' : ''} sélectionné${selectedContacts.length > 1 ? 's' : ''}`
                        )}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Zone de saisie et bouton d'envoi */}
                  <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={multiSelectMode ? "Message groupé..." : "Écrivez votre message..."}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      multiline
                      maxRows={4}
                    />
                    <IconButton
                      color="primary"
                      type="submit"
                      disabled={!newMessage.trim() || (multiSelectMode && selectedContacts.length === 0)}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}
              >
                <Typography variant="h6">
                  {multiSelectMode 
                    ? "Sélectionnez des contacts pour envoyer un message groupé" 
                    : "Sélectionnez un contact pour démarrer une conversation"}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MessageCenter;
